// PatientDocumentsSection.js - ADD TO PatientDetail.js

import React, { useState, useEffect } from 'react';
import { Upload, Download, Trash2, FileText, File, Image, X, Loader, AlertCircle, AlertTriangle } from 'lucide-react';
import ApiService from './ApiService';
import { usePermissions, PERMISSIONS } from './usePermissions';

const PatientDocumentsSection = ({ patient, currentUser }) => {
  const { hasPermission } = usePermissions(currentUser);
  
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState('other');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorModal, setErrorModal] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadDocuments();
  }, [patient.patientId]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const docs = await ApiService.getPatientDocuments(patient.patientId);
      setDocuments(docs);
    } catch (err) {
      console.error('Error loading documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setErrorModal({ title: 'File Too Large', message: 'File size must be less than 10MB. Please select a smaller file.' });
        return;
      }
      
      // Check file type
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/jpg',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        setErrorModal({ title: 'Invalid File Type', message: 'Only PDF, JPG, PNG, and DOC files are allowed.' });
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setErrorModal({ title: 'No File Selected', message: 'Please select a file to upload.' });
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(20);

      // Step 1: Get pre-signed URL
      const { uploadUrl, documentId } = await ApiService.getDocumentUploadUrl(
        patient.patientId,
        selectedFile.name,
        selectedFile.type
      );

      setUploadProgress(40);

      // Step 2: Upload to S3
      await ApiService.uploadFileToS3(uploadUrl, selectedFile);

      setUploadProgress(70);

      // Step 3: Confirm upload
      await ApiService.confirmDocumentUpload(documentId, selectedFile.size);

      setUploadProgress(100);

      // Reset and reload
      setSelectedFile(null);
      setShowUploadModal(false);
      setDocumentType('other');
      loadDocuments();
    } catch (err) {
      console.error('Error uploading document:', err);
      setErrorModal({ title: 'Upload Failed', message: 'Failed to upload document: ' + (err.message || 'Unknown error') });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleView = async (document) => {
    try {
      const { viewUrl } = await ApiService.getDocumentViewUrl(document.documentId);
      window.open(viewUrl, '_blank');
    } catch (err) {
      console.error('Error viewing document:', err);
      setErrorModal({ title: 'Error Viewing Document', message: err.message || 'Failed to view document. Please try again.' });
    }
  };

  const handleDelete = async (document) => {
    setDeleteConfirm(document);
  };

  const confirmDelete = async () => {
    try {
      await ApiService.deleteDocument(deleteConfirm.documentId);
      setDeleteConfirm(null);
      loadDocuments();
    } catch (err) {
      console.error('Error deleting document:', err);
      setDeleteConfirm(null);
      setErrorModal({ title: 'Error Deleting Document', message: err.message || 'Failed to delete document. Please try again.' });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (contentType) => {
    if (contentType.startsWith('image/')) {
      return <Image className="text-spruce-500" size={20} />;
    } else if (contentType === 'application/pdf') {
      return <FileText className="text-red-500" size={20} />;
    } else {
      return <File className="text-gray-500" size={20} />;
    }
  };

  const getDocumentTypeLabel = (filename) => {
    // You can enhance this with metadata from DynamoDB
    if (filename.toLowerCase().includes('consent')) return 'Consent Form';
    if (filename.toLowerCase().includes('intake')) return 'Intake Form';
    if (filename.toLowerCase().includes('insurance')) return 'Insurance';
    if (filename.toLowerCase().includes('lab')) return 'Lab Results';
    if (filename.toLowerCase().includes('prescription')) return 'Prescription';
    return 'Other';
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold">Documents</h2>
        {hasPermission(PERMISSIONS.PATIENTS_DOCUMENTS_ADD) && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-spruce-600 text-white px-4 py-2 rounded-lg hover:bg-spruce-700 flex items-center gap-2"
          >
            <Upload size={18} />
            Upload Document
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8">
          <Loader className="animate-spin mx-auto mb-2" size={32} />
          <p className="text-gray-500">Loading documents...</p>
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FileText className="mx-auto mb-4 text-gray-300" size={64} />
          <p className="mb-4">No documents uploaded yet</p>
          {hasPermission(PERMISSIONS.PATIENTS_DOCUMENTS_ADD) && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-spruce-600 text-white px-4 py-2 rounded-lg hover:bg-spruce-700"
            >
              Upload First Document
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Document</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Type</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Size</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Uploaded</th>
                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Uploaded By</th>
                <th className="text-right px-4 py-3 text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {documents.map((doc) => (
                <tr key={doc.documentId} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {getFileIcon(doc.contentType)}
                      <span className="text-sm font-medium text-gray-900">{doc.filename}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-spruce-100 text-spruce-800 rounded-full">
                      {getDocumentTypeLabel(doc.filename)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatFileSize(doc.fileSize)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {new Date(doc.uploadedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {doc.uploadedByName || doc.uploadedBy}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-2 justify-end">
                      {hasPermission(PERMISSIONS.PATIENTS_DOCUMENTS_VIEW) && (
                        <button
                          onClick={() => handleView(doc)}
                          className="p-2 text-spruce-600 hover:bg-spruce-50 rounded transition-colors"
                          title="View document"
                        >
                          <Download size={18} />
                        </button>
                      )}
                      {hasPermission(PERMISSIONS.PATIENTS_DOCUMENTS_DELETE) && (
                        <button
                          onClick={() => handleDelete(doc)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete document"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Upload size={24} />
                Upload Document
              </h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                }}
                className="text-gray-500 hover:text-gray-700"
                disabled={uploading}
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* File Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Document Type
                </label>
                <select
                  value={documentType}
                  onChange={(e) => setDocumentType(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  disabled={uploading}
                >
                  <option value="consent">Consent Form</option>
                  <option value="intake">Intake Form</option>
                  <option value="insurance">Insurance Card</option>
                  <option value="lab">Lab Results</option>
                  <option value="prescription">Prescription</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select File
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <input
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    disabled={uploading}
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload size={48} className="text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600">
                      {selectedFile ? selectedFile.name : 'Click to select file'}
                    </span>
                    <span className="text-xs text-gray-400 mt-1">
                      PDF, JPG, PNG, DOC (max 10MB)
                    </span>
                  </label>
                </div>
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Uploading...</span>
                    <span className="text-spruce-600">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-spruce-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Important Notice */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex gap-2">
                  <AlertCircle className="text-yellow-600 flex-shrink-0" size={20} />
                  <div className="text-xs text-yellow-800">
                    <strong>Important:</strong> Uploaded documents are encrypted and stored securely. 
                    Only authorized users can view patient documents.
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4">
                <button
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading}
                  className="flex-1 bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <Loader className="animate-spin" size={18} />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={18} />
                      Upload
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                  }}
                  disabled={uploading}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Delete Document?</h3>
            </div>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete <strong>{deleteConfirm.filename}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition flex items-center justify-center gap-2"
              >
                <Trash2 size={18} />
                Yes, Delete
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">{errorModal.title}</h3>
            </div>
            <p className="text-gray-600 mb-6">
              {errorModal.message}
            </p>
            <button
              onClick={() => setErrorModal(null)}
              className="w-full bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 transition"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientDocumentsSection;